import type { ReactNode } from 'react'
import { TrendingDown, TrendingUp } from 'lucide-react'
import { cx } from '@shared/lib/cx'
import { Card } from '../Card/Card'
import { FitMoneyValue } from '../FitMoneyValue/FitMoneyValue'
import {
  body,
  breakdown as breakdownClass,
  delta,
  header,
  helper as helperClass,
  iconTile,
  label,
  root,
  sparklineSlot,
  value as valueClass,
} from './StatCard.css'

export type StatCardProps = {
  label: string
  amount?: string
  currency?: string
  value?: ReactNode
  delta?: { label: string; positive?: boolean; tone?: 'positive' | 'negative' | 'neutral' }
  icon?: ReactNode
  helper?: ReactNode
  breakdown?: ReactNode
  sparkline?: ReactNode
  hero?: boolean
  className?: string
}

export function StatCard({
  label: labelText,
  amount,
  currency,
  value,
  delta: deltaInfo,
  icon,
  helper,
  breakdown,
  sparkline,
  hero = false,
  className,
}: StatCardProps) {
  const positive = deltaInfo?.positive ?? true
  const tone = deltaInfo?.tone ?? (positive ? 'positive' : 'negative')
  const hasMoneyValue = Boolean(amount && currency)
  const resolvedValue =
    value ??
    (hasMoneyValue ? (
      <FitMoneyValue
        amount={amount!}
        currency={currency!}
        maxSize={hero ? 40 : 26}
        minSize={hero ? 20 : 12}
        fractionSize={hero ? 'display' : 'xl'}
      />
    ) : null)

  return (
    <Card padding="md" className={cx(root({ hero }), className)}>
      <div className={header}>
        <span className={label}>{labelText}</span>
        {icon ? <span className={iconTile}>{icon}</span> : null}
      </div>
      <div className={body}>
        <div className={valueClass}>{resolvedValue}</div>
        {sparkline ? <div className={sparklineSlot}>{sparkline}</div> : null}
      </div>
      {breakdown ? <div className={breakdownClass}>{breakdown}</div> : null}
      {helper ? <span className={helperClass}>{helper}</span> : null}
      {deltaInfo ? (
        <div className={delta({ tone })}>
          {positive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
          {deltaInfo.label}
        </div>
      ) : null}
    </Card>
  )
}
